import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductTypeName } from '@electricjs/core_entity-client';
import { Flex, spaces, Switch, Text } from '@electricjs/arc';

import { PRODUCT_TYPES_BY_TYPE_GROUP } from '@/constants/storefront';
import { STOREFRONT_PAGE_SIZE } from '@/constants/pagination';
import { getCountryName } from '@/helpers/getCountryName';
import {
  useLazyGetAssetStorefrontQuery,
  useLazyListAssetsStorefrontQuery,
} from '@/redux/slices/assetApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

import { useStorefront } from '../../StorefrontProvider';

import { Store } from '@/types/storefront';
import { SelectionParamKeys } from '@/types/queryParamKeys';

import {
  ProductTypeGroupSelector,
  ProductTypeGroupSelectorProps,
  ItemList,
} from '../';
import { useItemDetailsModal } from '../useItemDetailsModal';

export type AssetStoreProps = Omit<
  ProductTypeGroupSelectorProps,
  'onProductTypesSelectFromGroup'
>;

export const AssetStore = ({
  selectedProductTypeGroup,
  onProductTypeGroupSelect,
  laptopRecommendationUrl,
}: AssetStoreProps) => {
  const { t } = useTranslation('common');

  const {
    assetCart,
    storefrontCheckoutForm,
    addAssetCartEntry,
    removeAssetCartEntry,
  } = useStorefront();

  const organizationId = useGetOrganizationId();

  const [selectedPage, setSelectedPage] = useState<number>(0);

  const [selectedProductTypes, setSelectedProductTypes] = useState<
    ProductTypeName[]
  >(PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup]); // initializing with the types from the selected group of types

  // Toggles `shippable_only`. Persists across product type filter changes.
  const [showNonShippableAssets, setShowNonShippableAssets] =
    useState<boolean>(false);

  const selectedShoppingCountry =
    storefrontCheckoutForm?.getValues('shoppingCountry');

  // Just for typescript, because this component will never be rendered in a use-case that the shopping country doesn't exist or is "other"
  if (!selectedShoppingCountry || selectedShoppingCountry.value === 'other') {
    throw new Error();
  }

  const shoppingCountryCode = selectedShoppingCountry.value;

  const [
    getPaginatedAssets,
    {
      data: paginatedAssets,
      isFetching: isAssetListLoading,
      isError: hasAssetListError,
      originalArgs: paginatedAssetsArgs,
    },
  ] = useLazyListAssetsStorefrontQuery();

  const assets = paginatedAssets?.results ?? [];
  const hasNonShippableAssets = Boolean(paginatedAssets?.totalNonShippable);
  // `paginatedAssetsArgs` (not `showNonShippableAssets`) drives this, since it only
  // updates in lockstep with `paginatedAssets` — using the live toggle state here
  // would briefly pair the new toggle value with the still-stale previous fetch's
  // data before the refetch kicks off, producing an incorrect intermediate count.
  const readyToShipCount =
    paginatedAssetsArgs?.shippableOnly === false
      ? (paginatedAssets?.total ?? 0) -
        (paginatedAssets?.totalNonShippable ?? 0)
      : (paginatedAssets?.total ?? 0);

  const applyFiltersSelections = useCallback(
    ({
      selectedPage,
      selectedProductTypes,
      shoppingCountryCode,
      shippableOnly,
    }: {
      selectedPage: number;
      selectedProductTypes: ProductTypeName[];
      shoppingCountryCode: string;
      shippableOnly: boolean;
    }) => {
      // fetch the assets based on the selections
      getPaginatedAssets(
        {
          organizationId,
          shoppingCountryCode,
          offset: selectedPage * STOREFRONT_PAGE_SIZE,
          limit: STOREFRONT_PAGE_SIZE,
          productTypes: selectedProductTypes,
          shippableOnly,
        },
        true
      )
        .unwrap()
        .catch(error => console.error('Error while fetching assets', error));
    },
    [getPaginatedAssets, organizationId]
  );

  const handleTypesSelectionFromGroup = useCallback(
    (types: ProductTypeName[]) => {
      setSelectedProductTypes([...types]);

      setSelectedPage(0);

      // `showNonShippableAssets` intentionally persists across product type changes.
    },
    []
  );

  const handlePageChange = (newPage: number) => {
    // only update the page (keep all filters and sorting)
    setSelectedPage(newPage);
  };

  const handleShowNonShippableAssetsChange = () => {
    setShowNonShippableAssets(previous => !previous);
    setSelectedPage(0);
  };

  // handling Shopping Country changes
  useEffect(() => {
    // only reset pagination (the carts will be cleared by the ShoppingCountrySelector component)
    setSelectedPage(0);
  }, [shoppingCountryCode]);

  // Applying filters/pagination/shippable-only selections
  useEffect(() => {
    applyFiltersSelections({
      selectedProductTypes,
      selectedPage,
      shoppingCountryCode: shoppingCountryCode,
      shippableOnly: !showNonShippableAssets,
    });
  }, [
    applyFiltersSelections,
    selectedProductTypes,
    selectedPage,
    shoppingCountryCode,
    showNonShippableAssets,
  ]);

  const [
    getAsset,
    { isFetching: isLoadingAssetDetails, isError: hasAssetDetailsError },
  ] = useLazyGetAssetStorefrontQuery();

  const getAssetDetails = useCallback(
    async (assetId: string) => {
      const asset = await getAsset(
        { organizationId, assetId, shoppingCountryCode: shoppingCountryCode },
        true
      ).unwrap();
      return asset;
    },
    [getAsset, organizationId, shoppingCountryCode]
  );

  useItemDetailsModal({
    items: assets,
    queryParam: SelectionParamKeys.AssetId,
    store: Store.Asset,
    loadingItemsList: isAssetListLoading,
    loadingItemDetails: isLoadingAssetDetails,
    onAddItem: addAssetCartEntry,
    onRemoveItem: removeAssetCartEntry,
    hasItemDetailsError: hasAssetDetailsError,
    itemDetailsErrorMessage: t('storefront.asset.assetDetailsError'),
    onItemDetailsAsyncRequest: getAssetDetails,
  });

  return (
    <Flex
      vertical
      rowGap={spaces.space_xl}
      width="100%"
      data-testid="asset-store">
      <ProductTypeGroupSelector
        selectedProductTypeGroup={selectedProductTypeGroup}
        onProductTypeGroupSelect={onProductTypeGroupSelect}
        onProductTypesSelectFromGroup={handleTypesSelectionFromGroup}
        laptopRecommendationUrl={laptopRecommendationUrl}
      />
      <Flex rowGap={spaces.space_xl} width="100%" alignItems={'center'}>
        {Boolean(paginatedAssets?.total) && (
          <Flex flex={1}>
            {!isAssetListLoading && (
              <Text>
                {t('storefront.asset.assetsReadyToShip', {
                  count: readyToShipCount,
                  country: getCountryName(shoppingCountryCode),
                })}
              </Text>
            )}
          </Flex>
        )}
        {hasNonShippableAssets && (
          <Flex alignItems="center">
            <Text>
              {t('storefront.asset.showStoredOutside', {
                count: paginatedAssets?.totalNonShippable ?? 0,
                country: getCountryName(shoppingCountryCode),
              })}
            </Text>
            <Switch
              id="assets-show-non-shippable-switch"
              checked={showNonShippableAssets}
              onChange={handleShowNonShippableAssetsChange}
            />
          </Flex>
        )}
      </Flex>
      <ItemList
        id="asset-list"
        store={Store.Asset}
        items={assets}
        onAddItem={addAssetCartEntry}
        onRemoveItem={removeAssetCartEntry}
        totalItemsResult={paginatedAssets?.total ?? 0}
        selectedPage={selectedPage}
        onPageChange={handlePageChange}
        isLoading={isAssetListLoading}
        hasError={hasAssetListError}
        errorMessage={t('storefront.asset.assetListError')}
        selectedItemsIds={assetCart.map(entry => entry.item.id)}
      />
    </Flex>
  );
};
